import { ProCard } from '@ant-design/pro-components';
import AdminProTable from '@/components/admin-pro-table';
import { useRequest, useSetState } from 'ahooks';
import { App, Badge, Button, DatePicker, Flex, Form, Select } from 'antd';
import dayjs from 'dayjs';
import { isEmpty, omitBy } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Permission from '@/components/permission';

import OperationLogDrawer from '@/components/OperationLogDrawer';
import { exports } from '@/services/exports';
import { operators } from '@/services/operator';

import type { ProTableProps } from '@ant-design/pro-components';
import type { FormProps, PaginationProps } from 'antd';
import type { ExportParams, ExportResponse } from '@/services/exports';
import type { OperatorsResponse } from '@/services/operator';

type State = {
    dataSource: ExportResponse[];
    operators: OperatorsResponse[];
};

const PageExports: React.FC = () => {
    const { message } = App.useApp();
    const [formSearch] = Form.useForm();
    const { formatMessage } = useIntl();
    const searchRef = useRef<HTMLDivElement>(null);

    const [state, setState] = useSetState<State>({
        dataSource: [],
        operators: [],
    });

    const [pagination, setPagination] = useSetState<PaginationProps>({
        current: 1,
        pageSize: 20,
        total: 0,
        onChange: (page, pageSize) => {
            setPagination({
                current: page,
                pageSize,
            });
        },
    });

    const [payload, setPayload] = useSetState<ExportParams>({
        create_end: dayjs().endOf('d').format(),
        create_start: dayjs().startOf('d').format(),
        operator_account_id: undefined,
    });
    const [logDrawerOpen, setLogDrawerOpen] = useState(false);
    const [logBizId, setLogBizId] = useState<string | null>(null);

    useRequest(operators, {
        defaultParams: [{ limit: 9999, page: 1 }],
        onSuccess: ({ data: { code, data, msg } }) => {
            if (code !== 200) {
                return message.error(msg);
            }
            setState({
                operators: (data as any)?.list ?? [],
            });
        },
    });

    const {
        loading: loadingExports,
        refresh: refreshExports,
        run: runExports,
    } = useRequest(exports, {
        manual: true,
        onSuccess: ({ data: { code, data, msg } }) => {
            if (code !== 200) {
                return message.error(msg);
            }
            const meta = (data as any)?.meta ?? {};
            const list = (data as any)?.list ?? [];
            setPagination((prev) => ({
                ...prev,
                current: meta.currentPage ?? prev.current,
                pageSize: meta.perPage ?? prev.pageSize,
                total: meta.total ?? list.length,
            }));
            setState({
                dataSource: list,
            });
        },
    });

    const formSearchProps: FormProps = {
        form: formSearch,
        initialValues: {
            date: [dayjs().startOf('d'), dayjs().endOf('d')],
        },
        layout: 'inline',
        onFinish: (values) => {
            if (values.date?.length === 2) {
                values.create_end = values.date[1].endOf('d').format();
                values.create_start = values.date[0].startOf('d').format();
            }
            delete values.date;
            setPagination((prev) => ({ ...prev, current: 1 }));
            setPayload({
                create_end: undefined,
                create_start: undefined,
                operator_account_id: undefined,
                ...omitBy(values, isEmpty),
            });
        },
    };

    const proTableProps: ProTableProps<ExportResponse, any> = {
        bordered: true,
        columns: [
            {
                dataIndex: 'index',
                fixed: 'left',
                title: 'No.',
                valueType: 'index',
                width: 50,
            },
            {
                dataIndex: 'origin',
                ellipsis: true,
                title: <FormattedMessage id="p.exports.t.c.exportContent" />,
            },
            {
                dataIndex: 'status',
                title: <FormattedMessage id="t.c.status" />,
                width: 120,
                valueEnum: {
                    pending: <Badge status="processing" text={<FormattedMessage id="p.exports.status.processing" />} />,
                    failed: <Badge status="error" text={<FormattedMessage id="p.exports.status.failed" />} />,
                    completed: <Badge status="success" text={<FormattedMessage id="p.exports.status.completed" />} />,
                },
            },
            {
                dataIndex: ['operator_account', 'name'],
                ellipsis: true,
                title: <FormattedMessage id="t.c.operatedBy" />,
                width: 120,
            },
            {
                dataIndex: 'created_at',
                title: <FormattedMessage id="t.c.operatedAt" />,
                valueType: 'dateTime',
                width: 200,
            },
            {
                fixed: 'right',
                key: 'option',
                title: <FormattedMessage id="t.c.action" />,
                valueType: 'option',
                width: 220,
                render: (_, record) => {
                    const id = (record as any).id;
                    return (
                        <>
                            {record.status === 'completed' && (
                                <a href={record.download_url} target="_blank" rel="noreferrer" style={{ marginRight: 8 }}>
                                    <FormattedMessage id="b.download" />
                                </a>
                            )}
                            {id && (
                                <Button
                                    type="link"
                                    style={{ padding: 0 }}
                                    onClick={() => {
                                        setLogBizId(id);
                                        setLogDrawerOpen(true);
                                    }}
                                >
                                    操作记录
                                </Button>
                            )}
                        </>
                    );
                },
            },
        ],
        dataSource: state.dataSource,
        loading: loadingExports,
        options: {
            reload: refreshExports,
        },
        pagination,
        rowKey: 'id',
        scroll: {
            x: 870,
            y: `calc(100vh - 224px - ${searchRef.current?.clientHeight || 64}px - 16px - 48px)`,
        },
        search: false,
    };

    useEffect(() => {
        runExports({
            ...payload,
            limit: pagination.pageSize,
            page: pagination.current,
        });
    }, [pagination.current, pagination.pageSize, payload]);

    return (
        <>
            <ProCard className="mb-16" ref={searchRef}>
                <Form {...formSearchProps}>
                    <Flex gap={16} wrap={true}>
                        <Permission permission="System-Management_Downloads_All-Data">
                            <Form.Item name="operator_account_id" style={{ marginRight: 0 }}>
                                <Select
                                    allowClear
                                    optionFilterProp="label"
                                    options={state.operators.map((op) => ({
                                        label: op.account?.name,
                                        value: op.account?.id,
                                    }))}
                                    placeholder={<FormattedMessage id="t.c.operatedBy" />}
                                    showSearch
                                    style={{ width: 200 }}
                                />
                            </Form.Item>
                        </Permission>
                        <Form.Item label={formatMessage({ id: 't.c.operatedAt' })} name="date" style={{ marginRight: 0 }}>
                            <DatePicker.RangePicker allowClear />
                        </Form.Item>
                        <Button loading={loadingExports} type="primary" onClick={() => formSearch.submit()}>
                            <FormattedMessage id="b.search" />
                        </Button>
                        <Button onClick={() => formSearch.resetFields()}>
                            <FormattedMessage id="b.reset" />
                        </Button>
                    </Flex>
                </Form>
            </ProCard>
            <AdminProTable {...proTableProps} />
            <OperationLogDrawer
                open={logDrawerOpen}
                bizId={logBizId}
                onClose={() => {
                    setLogDrawerOpen(false);
                    setLogBizId(null);
                }}
            />
        </>
    );
};

export default PageExports;
